import { toNestErrors } from '@hookform/resolvers';
import Ajv, { ErrorObject } from 'ajv';
import addFormats from 'ajv-formats';
import { FieldError, Resolver } from 'react-hook-form';
import { ERROR_MESSAGES } from '~/constants';

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

// [MCV-1263] AJV's default phrasing for a missing required field is
// "must have required property 'X'" — too technical for end users. Map the
// common keywords to the same short messages used by the Zod-driven forms
// so attestation errors read like the rest of the vault.
const friendlyMessage = (error: ErrorObject): string => {
  if (error.keyword === 'required') return ERROR_MESSAGES.REQUIRED;
  return error.message || 'Validation error';
};

// [MCV-1262] AJV emits structural/combinator errors (`if`, `then`, `else`,
// `anyOf`, `oneOf`, `allOf`, `not`) at the *parent* container path whenever
// a composition fails. The underlying field-level error (e.g. the `required`
// error for the specific missing property) is emitted alongside at a deeper
// path. Keeping the parent-level structural error causes `toNestErrors` to
// treat the parent as a FieldError leaf, which shadows the nested field
// error we actually want to display. Drop them.
const STRUCTURAL_KEYWORDS = new Set(['if', 'then', 'else', 'anyOf', 'oneOf', 'allOf', 'not']);

export const ajvResolver =
  (schema: object): Resolver =>
  async (data, _context, options) => {
    const validate = ajv.compile(schema);
    const valid = validate(data);

    if (valid) {
      return { values: data, errors: {} };
    }

    const formattedErrors: Record<string, FieldError> = {};
    // Convert ajv errors to react-hook-form format
    validate.errors?.forEach((error) => {
      if (STRUCTURAL_KEYWORDS.has(error.keyword)) return;

      let instancePath = error.instancePath;

      if (error.keyword === 'required') {
        const missingProperty = (error.params as { missingProperty?: string }).missingProperty;
        if (missingProperty) {
          instancePath = `${instancePath}/${missingProperty}`;
        }
      }

      const fieldName = instancePath.replace(/^\//, '').replace(/\//g, '.') || error.keyword;
      if (formattedErrors[fieldName]) {
        return;
      }
      formattedErrors[fieldName] = {
        type: error.keyword,
        message: friendlyMessage(error),
      } as FieldError;
    });

    return {
      values: {},
      errors: toNestErrors(formattedErrors, options),
    };
  };
